import {
  isMultiErrorResponse,
  isNetworkError,
  isStringErrorResponse,
} from '@monorepo/shared/apiClient/httpClient';
import { FetchUsersResponse } from '@monorepo/shared/apiClient/users';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { useOrganizationUsers } from '@monorepo/shared/hooks/useOrgUsers';
import { SelectableOption } from '@monorepo/shared/types/SelectableOption';
import { ProjectPermissionRoles } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useInviteUserToProject } from '../hooks/useInviteUserToProject';
import { inviteRoleOptions } from './changeUserRoleUtils';
import {
  formatUserNameWithEmail,
  INVITE_CARRIER_PIGEON_ERROR,
  SUCCESS_TOAST_DURATION_MS,
  trackEmailInviteInPendo,
} from './inviteUserUtils';
import { InviteUserEmailField } from './InviteUserEmailField';
import {
  UserSiteAccessFormValues,
  UserSiteAccessModal,
} from './UserSiteAccessModal';

interface InviteUserModalProps {
  organizationId: string;
  projectId: string;
  projectName: string;
  projectUsers: FetchUsersResponse;
  onClose: () => void;
}

interface FormValues extends UserSiteAccessFormValues {
  email: string;
  name: string;
}

function getSuccessMessage(result: {
  isSsoUser: boolean;
  isNewUser: boolean;
  isInvitationCreated: boolean;
}): string | JSX.Element | null {
  if (result.isSsoUser && result.isNewUser) {
    return (
      <div>
        <strong>The user has been added to the site.</strong>
        <br />
        No invitation email was sent — they sign in with their company
        credentials via Single Sign-On (SSO). Reach out to your IT team if you
        need help setting up access.
      </div>
    );
  }

  if (result.isInvitationCreated) {
    return 'The user has been added to the site and an invitation was created. An email will be sent within 10 minutes.';
  }

  return 'The user has been added to the site but already exists in Mapistry so an invitation will not be sent.';
}

export function InviteUserModal({
  organizationId,
  projectId,
  projectName,
  projectUsers,
  onClose,
}: InviteUserModalProps) {
  const { error: showError, showUserFriendlyErrorToast, success } = useToast();

  const { users: organizationUsers, isLoading: isLoadingOrgUsers } =
    useOrganizationUsers({ organizationId });

  const [inviteUser] = useInviteUserToProject({
    config: { throwOnError: true },
  });

  const projectUserEmails = useMemo(
    () =>
      new Set(projectUsers.map((user) => user.userEmail.trim().toLowerCase())),
    [projectUsers],
  );

  const { emailOptions, orgUsersByEmail } = useMemo(() => {
    const byEmail = new Map<string, { name: string; email: string }>();
    const options: SelectableOption[] = [];

    (organizationUsers ?? []).forEach((user) => {
      const email = user.email.trim().toLowerCase();
      if (projectUserEmails.has(email)) {
        return;
      }
      byEmail.set(email, { name: user.name, email: user.email });
      options.push({
        label: formatUserNameWithEmail(user.name, user.email),
        value: user.email,
      });
    });

    options.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true }),
    );
    return { emailOptions: options, orgUsersByEmail: byEmail };
  }, [organizationUsers, projectUserEmails]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const email = values.email.trim();
      const name = values.name.trim();
      try {
        const result = await inviteUser({
          projectId,
          email,
          name: name || undefined,
          role: values.role,
        });
        if (!result) {
          throw new Error('Invite user response was empty.');
        }
        trackEmailInviteInPendo({
          inviteResult: result,
          userName: name,
          userRole: values.role,
          userEmail: email,
          projectId,
          projectName,
        });
        const message = getSuccessMessage(result);
        if (message) {
          success(message, { autoHideDuration: SUCCESS_TOAST_DURATION_MS });
        }
        onClose();
      } catch (err) {
        if (isNetworkError(err) && err.status === 403) {
          showError(
            'Only Site Administrators can invite users to this project.',
          );
        } else if (isMultiErrorResponse(err)) {
          showError(`${err.data.message}: ${err.data.errors.join('; ')}.`);
        } else if (isStringErrorResponse(err) && err.status === 422) {
          showError(err.data);
        } else {
          showUserFriendlyErrorToast(err, INVITE_CARRIER_PIGEON_ERROR);
        }
      }
    },
    [
      inviteUser,
      onClose,
      projectId,
      projectName,
      showError,
      showUserFriendlyErrorToast,
      success,
    ],
  );

  return (
    <UserSiteAccessModal<FormValues>
      title="Invite new user"
      description="The invited user will set their name and job title when signing up."
      emailSlot={
        <InviteUserEmailField
          options={emailOptions}
          orgUsersByEmail={orgUsersByEmail}
          isLoading={isLoadingOrgUsers}
        />
      }
      roleOptions={inviteRoleOptions}
      initialValues={{
        email: '',
        name: '',
        role: ProjectPermissionRoles.PROJECT_CONTRIBUTOR,
      }}
      onSubmit={onSubmit}
      onClose={onClose}
      submitLabel="Invite"
      submitPendingLabel="Sending..."
    />
  );
}
